export const FOLDER_MIME_TYPE = 'application/vnd.google-apps.folder';

/** The part of Drive's `imageMediaMetadata` we request (see FILE_FIELDS in driveApi.ts). */
export interface ImageMediaMetadata {
    width?: number;
    height?: number;
    /** Clockwise 90° rotations applied from the image's original orientation. */
    rotation?: number;
    /** EXIF capture time, formatted "YYYY:MM:DD HH:MM:SS". */
    time?: string;
    cameraMake?: string;
    cameraModel?: string;
    lens?: string;
    /** Seconds. */
    exposureTime?: number;
    /** The f-number. */
    aperture?: number;
    isoSpeed?: number;
    /** Millimetres. */
    focalLength?: number;
    location?: { latitude?: number; longitude?: number; altitude?: number };
}

export interface DriveFile {
    id: string;
    name: string;
    mimeType: string;
    parents?: string[];
    thumbnailLink?: string;
    size?: string;
    createdTime?: string;
    modifiedTime?: string;
    /** Drive's per-file version number; changes on every change to the file. */
    version?: string;
    owners?: { displayName: string }[];
    imageMediaMetadata?: ImageMediaMetadata;
    /** What the signed-in user may do with the file (only the capabilities we request). */
    capabilities?: { canDelete?: boolean };
}

export interface DriveDriveInfo {
    id: string;
    name: string;
}

export function isFolder(file: DriveFile): boolean {
    return file.mimeType === FOLDER_MIME_TYPE;
}

export function isImageFile(file: DriveFile): boolean {
    return !isFolder(file) && (!file.mimeType || file.mimeType.startsWith('image/'));
}

export const BIN_NAMES = ['web', 'archive', 'plazma'] as const;
export type BinName = typeof BIN_NAMES[number];

export function isBinName(value: string): value is BinName {
    return (BIN_NAMES as readonly string[]).includes(value);
}

export interface DriveUser {
    displayName?: string;
    photoLink?: string;
    /** True when this user is the one the request was authenticated as. */
    me?: boolean;
}

export interface DriveReply {
    id: string;
    content?: string;
    createdTime: string;
    deleted?: boolean;
    /** Set on replies that resolve or reopen their parent comment. */
    action?: 'resolve' | 'reopen';
    author?: DriveUser;
}

export interface DriveComment {
    id: string;
    content?: string;
    /** What the comment refers to, as JSON of the creating app's own format (see `parseRectAnchor`). */
    anchor?: string;
    createdTime: string;
    resolved?: boolean;
    deleted?: boolean;
    author?: DriveUser;
    replies?: DriveReply[];
}

/** A stored version of a file's content (Drive Revisions API). */
export interface DriveRevision {
    id: string;
    modifiedTime: string;
    mimeType?: string;
    originalFilename?: string;
    size?: string;
    keepForever?: boolean;
    lastModifyingUser?: DriveUser;
}
